import { useState } from "react";
import axios from "axios";
import {
  createOrganization,
  type CreateOrganizationPayload,
} from "../../api/organization.api";
import { ENVIRONMENT } from "../../constants";
import { getDefaultCreateOrganizationInput } from "../../constants/organization-defaults.constants";
import { usePassword } from "../../hooks/use-password.hook";
import type { CreateOrganizationInput } from "../../types/organization.type";
import {
  ORGANIZATION_FORM_SECTIONS,
  type FormFieldDef,
} from "./organization-form-fields.constants";

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string; error?: string }
      | undefined;
    return data?.message ?? data?.error ?? error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Something went wrong.";
}

function parseStringList(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatStringList(value: string[]): string {
  return value.join(", ");
}

function toDateInputValue(value: Date | null | undefined): string {
  if (!value) {
    return "";
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toISOString().slice(0, 10);
}

function deriveLoadsForwardingEmail(name: string): string {
  const domain =
    ENVIRONMENT === "staging"
      ? "loads-dev.loadhive-dev.com"
      : "loads.loadhive.com";
  return `${name.trim().toLowerCase()}@${domain}`;
}

function normalizeForSubmit(
  form: CreateOrganizationInput
): CreateOrganizationPayload {
  const nullableKeys: (keyof CreateOrganizationInput)[] = [
    "mcNumber",
    "primaryColor",
    "secondaryColor",
    "tabColor",
    "logoUrl",
    "slackBusinessWebhook",
    "redirectUrlPrivacyPolicy",
    "redirectUrlTermsOfService",
    "redirectUrlEula",
    "redirectUrlIosAppStore",
    "redirectUrlAndroidPlayStore",
    "redirectUrlDriverAgreement",
    "redirectUrlDriverAgreementPreview",
    "driverAgreementClientUserId",
  ];

  const next: CreateOrganizationInput = { ...form };
  for (const key of nullableKeys) {
    const value = next[key];
    if (typeof value === "string" && value.trim() === "") {
      (next as Record<string, unknown>)[key] = null;
    }
  }

  return {
    ...next,
    loadsForwardingEmail: deriveLoadsForwardingEmail(next.name),
  };
}

export function CreateOrganizationForm() {
  const password = usePassword();
  const [form, setForm] = useState<CreateOrganizationInput>(
    getDefaultCreateOrganizationInput
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  function updateField<K extends keyof CreateOrganizationInput>(
    key: K,
    value: CreateOrganizationInput[K]
  ) {
    setForm((current) => ({ ...current, [key]: value }));
    if (error) {
      setError("");
    }
    if (successMessage) {
      setSuccessMessage("");
    }
  }

  function toggleEnumListValue(
    key: keyof CreateOrganizationInput,
    option: string,
    checked: boolean
  ) {
    const current = (form[key] as string[]) ?? [];
    const next = checked
      ? [...new Set([...current, option])]
      : current.filter((item) => item !== option);
    updateField(key, next as CreateOrganizationInput[typeof key]);
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");
    setSuccessMessage("");

    const payload = normalizeForSubmit(form);
    createOrganization(password, payload)
      .then((data) => {
        const created = data.organization;
        setSuccessMessage(
          `Created organization "${created.displayName || created.name}" (${created.organizationId}).`
        );
        setForm(getDefaultCreateOrganizationInput());
      })
      .catch((submitError) => {
        setError(getErrorMessage(submitError));
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  function renderField(field: FormFieldDef) {
    const id = `org-field-${field.key}`;
    const value = form[field.key];

    if (field.type === "boolean") {
      return (
        <label key={field.key} className="org-field org-field-checkbox" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={Boolean(value)}
            disabled={isSubmitting}
            onChange={(event) =>
              updateField(field.key, event.target.checked as never)
            }
          />
          <span>{field.label}</span>
        </label>
      );
    }

    if (field.type === "enumList") {
      const selected = (value as string[]) ?? [];
      return (
        <fieldset key={field.key} className="org-field org-field-enum-list">
          <legend>{field.label}</legend>
          <div className="org-enum-list">
            {(field.options ?? []).map((option) => {
              const optionId = `${id}-${option}`;
              return (
                <label key={option} htmlFor={optionId}>
                  <input
                    id={optionId}
                    type="checkbox"
                    checked={selected.includes(option)}
                    disabled={isSubmitting}
                    onChange={(event) =>
                      toggleEnumListValue(field.key, option, event.target.checked)
                    }
                  />
                  <span>{option}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      );
    }

    return (
      <label key={field.key} className="org-field" htmlFor={id}>
        <span className="org-field-meta">
          <span className="org-field-label">
            {field.label}
            {field.required ? (
              <span className="org-field-required" aria-hidden="true">
                {" "}
                *
              </span>
            ) : null}
          </span>
          <span className="org-field-hint">{field.hint ?? ""}</span>
        </span>
        {field.type === "enum" ? (
          <select
            id={id}
            value={String(value ?? "")}
            disabled={isSubmitting}
            required={field.required}
            onChange={(event) =>
              updateField(field.key, event.target.value as never)
            }
          >
            {(field.options ?? []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : field.type === "stringList" ? (
          <input
            id={id}
            type="text"
            value={formatStringList((value as string[]) ?? [])}
            disabled={isSubmitting}
            required={field.required}
            onChange={(event) =>
              updateField(field.key, parseStringList(event.target.value) as never)
            }
          />
        ) : field.type === "number" ? (
          <input
            id={id}
            type="number"
            value={Number(value ?? 0)}
            disabled={isSubmitting}
            required={field.required}
            onChange={(event) =>
              updateField(field.key, Number(event.target.value) as never)
            }
          />
        ) : field.type === "nullableNumber" ? (
          <input
            id={id}
            type="number"
            value={value == null ? "" : Number(value)}
            disabled={isSubmitting}
            onChange={(event) => {
              const raw = event.target.value;
              updateField(
                field.key,
                (raw === "" ? null : Number(raw)) as never
              );
            }}
          />
        ) : field.type === "nullableDate" ? (
          <input
            id={id}
            type="date"
            value={toDateInputValue(value as Date | null | undefined)}
            disabled={isSubmitting}
            onChange={(event) => {
              const raw = event.target.value;
              updateField(
                field.key,
                (raw ? new Date(`${raw}T00:00:00.000Z`) : null) as never
              );
            }}
          />
        ) : (
          <input
            id={id}
            type={
              field.type === "email"
                ? "email"
                : field.type === "url"
                  ? "url"
                  : "text"
            }
            value={value == null ? "" : String(value)}
            disabled={isSubmitting}
            required={field.required}
            onChange={(event) =>
              updateField(field.key, event.target.value as never)
            }
          />
        )}
      </label>
    );
  }

  return (
    <form className="organization-form" onSubmit={handleSubmit}>
      {ORGANIZATION_FORM_SECTIONS.map((section) => (
        <section key={section.id} className="org-form-section">
          <h2>{section.title}</h2>
          <div className="org-form-grid">{section.fields.map(renderField)}</div>
        </section>
      ))}

      {error && (
        <p className="org-form-error" role="alert">
          {error}
        </p>
      )}
      {successMessage && (
        <p className="org-form-success" role="status">
          {successMessage}
        </p>
      )}

      <div className="org-form-actions">
        <button
          type="button"
          className="org-form-secondary"
          disabled={isSubmitting}
          onClick={() => {
            setForm(getDefaultCreateOrganizationInput());
            setError("");
            setSuccessMessage("");
          }}
        >
          Reset defaults
        </button>
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Creating…" : "Create organization"}
        </button>
      </div>
    </form>
  );
}
